// app/components/AdvancedChart/components/IndicatorGuide.tsx

/**
 * Function to show the indicators guide modal
 * This creates a centered popup with markdown content about trading indicators
 */
export const showIndicatorsGuide = () => {
  // Create and show a modal with the markdown content
  const modal = document.createElement('div');
  modal.style.position = 'fixed';
  modal.style.top = '50%';
  modal.style.left = '50%';
  modal.style.transform = 'translate(-50%, -50%)';
  modal.style.backgroundColor = '#1e2329';
  modal.style.padding = '20px';
  modal.style.borderRadius = '8px';
  modal.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
  modal.style.zIndex = '99999';
  modal.style.maxWidth = '80vw';
  modal.style.maxHeight = '80vh';
  modal.style.width = '90%';
  modal.style.height = '90%';
  modal.style.color = '#e0e0e0';
  modal.style.overflow = 'hidden';
  modal.style.display = 'flex';
  modal.style.flexDirection = 'column';
  
  // Create overlay
  const overlay = document.createElement('div');
  overlay.style.position = 'fixed';
  overlay.style.top = '0';
  overlay.style.left = '0';
  overlay.style.width = '100%';
  overlay.style.height = '100%';
  overlay.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
  overlay.style.zIndex = '99998';
  
  // Fetch and display the markdown content
  fetch('/api/guide/indicators-guide')
    .then(response => {
      if (!response.ok) {
        // Fallback to reading the file directly if API doesn't exist
        return fetch('/components/AdvancedChart/HOW_INDICATORS_WORK.md')
          .then(res => res.ok ? res.text() : Promise.reject('File not found'));
      }
      return response.text();
    })
    .then(markdownContent => {
      // Convert markdown to HTML (basic conversion)
      const htmlContent = markdownContent
        .replace(/^# (.*$)/gim, '<h1 style="color: #88a1ac; margin-top: 0;">$1</h1>')
        .replace(/^## (.*$)/gim, '<h2 style="color: #88a1ac; margin-top: 20px;">$1</h2>')
        .replace(/^### (.*$)/gim, '<h3 style="color: #88a1ac; margin-top: 15px;">$1</h3>')
        .replace(/\*\*(.*?)\*\*/g, '<strong style="color: #88a1ac;">$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/`(.*?)`/g, '<code style="background: #151a1e; padding: 2px 4px; border-radius: 3px; color: #88a1ac;">$1</code>')
        .replace(/^- (.*$)/gim, '<li style="margin: 5px 0;">$1</li>')
        .replace(/\n\n/g, '</p><p style="margin: 10px 0;">')
        .replace(/^(?!<[h|l|p])/gm, '<p style="margin: 10px 0;">')
        .replace(/(?<!>)$/gm, '</p>');
      
      modal.innerHTML = `
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; border-bottom: 1px solid #333; padding-bottom: 10px;">
          <h2 style="margin: 0; color: #88a1ac;">How Trading Indicators Work</h2>
          <button id="close-indicators-modal" style="background: #88a1ac; border: none; padding: 8px 12px; border-radius: 4px; cursor: pointer; color: #151a1e; font-size: 16px; font-weight: bold;">×</button>
        </div>
        <div style="flex: 1; overflow-y: auto; padding-right: 10px;">
          ${htmlContent}
        </div>
      `;
    })
    .catch(error => {
      console.error('Error loading indicators guide:', error);
      // Fallback content
      modal.innerHTML = `
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; border-bottom: 1px solid #333; padding-bottom: 10px;">
          <h2 style="margin: 0; color: #88a1ac;">How Trading Indicators Work</h2>
          <button id="close-indicators-modal" style="background: #88a1ac; border: none; padding: 8px 12px; border-radius: 4px; cursor: pointer; color: #151a1e; font-size: 16px; font-weight: bold;">×</button>
        </div>
        <div style="flex: 1; overflow-y: auto; padding-right: 10px;">
          <h3 style="color: #88a1ac;">Indicators Guide</h3>
          <p>This guide explains how to use the technical indicators in the Advanced Chart component.</p>
          <p>The detailed guide is available in the HOW_INDICATORS_WORK.md file in the AdvancedChart component folder.</p>
          <h4 style="color: #88a1ac;">Available Indicators:</h4>
          <ul>
            <li><strong>RSI</strong> - Relative Strength Index for momentum analysis</li>
            <li><strong>MACD</strong> - Moving Average Convergence Divergence for trend analysis</li>
            <li><strong>Stochastic</strong> - Stochastic Oscillator for overbought/oversold conditions</li>
            <li><strong>ATR</strong> - Average True Range for volatility measurement</li>
            <li><strong>MFI</strong> - Money Flow Index for volume-weighted momentum</li>
            <li><strong>Force Index</strong> - Force Index for price and volume analysis</li>
          </ul>
        </div>
      `;
    })
    .finally(() => {
      // Add event listeners after content is loaded
      setTimeout(() => {
        const closeButton = document.getElementById('close-indicators-modal');
        if (closeButton) {
          closeButton.addEventListener('click', () => {
            if (document.body.contains(overlay)) {
              document.body.removeChild(overlay);
            }
            if (document.body.contains(modal)) {
              document.body.removeChild(modal);
            }
          });
        }
        
        // Close on overlay click
        overlay.addEventListener('click', (e) => {
          if (e.target === overlay) {
            if (document.body.contains(overlay)) {
              document.body.removeChild(overlay);
            }
            if (document.body.contains(modal)) {
              document.body.removeChild(modal);
            }
          }
        });
        
        // Close on escape key
        const handleEscape = (e: KeyboardEvent) => {
          if (e.key === 'Escape') {
            if (document.body.contains(overlay)) {
              document.body.removeChild(overlay);
            }
            if (document.body.contains(modal)) {
              document.body.removeChild(modal);
            }
            document.removeEventListener('keydown', handleEscape);
          }
        };
        document.addEventListener('keydown', handleEscape);
      }, 100);
    });
  
  document.body.appendChild(overlay);
  document.body.appendChild(modal);
};